import type { TOptions, TPlayer } from 'video.js'
import type { ReceiverCustomData } from './index.h'

import { createChromecastButton } from './ChromecastButton'
import * as chromecast from './plugin/index'

// plugin documentation -> https://developers.google.com/cast/docs/overview
//                      -> https://developers.google.com/cast/docs/reference
//

/**
 * Key to store chromecast configuration in player options
 */
const chromecastKey = '~~chromecast'

/**
 * Chromecast plugin configuration options
 */
export type ChromecastPluginOptions = {
  // id of a custom chromecast receiver app to use
  // defaults to the default media receiver id
  receiverAppID?: string
}

/**
 * Chromecast tech configuration options
 */
export type ChromecastTechOptions = {
  // function that this plugin calls when it needs a string that will be the title shown in the UI
  // that is shown when a Chromecast session is active and connected. When the this plugin calls
  // the `requestTitleFn`, it passes it the current source object and expects a string in return.
  // If nothing is returned or if this option is not defined, no title will be shown.
  requestTitleFn?: (source: any) => string | undefined

  // function that this plugin calls when it needs a string that will be the sub-title shown in the UI
  // that is shown when a Chromecast session is active and connected. When the this plugin calls
  // the `requestSubtitleFn`, it passes it the current source object and expects a string in return.
  // If nothing is returned or if this option is not defined, no sub-title will be shown.
  requestSubtitleFn?: (source: any) => string | undefined

  // function that this plugin calls when it needs an object that contains custom information necessary
  // for a Chromecast receiver app when a session is active and connected. When the this plugin calls
  // the `requestCustomDataFn`, it passes it the current source object and expects an object in return.
  // If nothing is returned or if this option is not defined, no custom data will be sent.
  // This option is intended to be used with a custom receiver application to extend its default capabilities.
  requestCustomDataFn?: (source: any) => ReceiverCustomData

  // On error handlers
  onTrackSetSuccess?: () => void
  onTrackSetError?: () => void
  onMediaInfoLoadError?: (reason: string) => void

  // Lifecycle callbacks of cast feature
  onConnected?: () => void
  onDisconnected?: (time: number) => void
}

/**
 * Chromecast configuration
 */
export type ChromecastConfiguration = {
  enabled?: boolean
} & ChromecastTechOptions

/**
 * Flag to check if chromecast addon was initialized
 * This is a global flag intentionally, not per player, because it shows
 * if the chromecast framework was loaded, and it should be loaded only once
 */
let initialized: boolean | Promise<boolean> = false

/**
 * Initialize chromecast plugin
 * - add chromecast plugin to VideoJS
 * - inject chromecast framework
 */
function init(): void | boolean | Promise<boolean> {
  // browser not supported
  if (!chromecast.isChromiumBased()) return true

  if (initialized) return initialized
  initialized = true

  createChromecastButton()

  if (!window.cast?.framework)
    return (initialized = chromecast.getReady()).then(
      () => (initialized = true)
    )
}

/**
 * Configure chromecast plugin before player initialization
 */
export function configure(configuration?: ChromecastConfiguration) {
  const configurator = (options: TOptions): TOptions => {
    // store configuration in player options
    options[chromecastKey] = configuration || {}

    // do nothing if chromecast is disabled
    if (!options[chromecastKey].enabled) return options

    // add chromecast to techOrder
    // > Important: In addition to defining plugin configuration, you are required to define
    // > the player's techOrder option, setting 'chromecast' as the first Tech in the list.
    const techOrder: string[] = options.techOrder?.length
      ? [...options.techOrder]
      : ['Html5']
    techOrder.unshift('chromecast')

    // return new options with chromecast in techOrder
    return {
      ...options,
      techOrder,
      chromecast: configuration,
    }
  }

  // initialize chromecast plugin
  // this is done only once, on the first player creation
  let initializator: void | boolean | Promise<boolean> = undefined
  if (configuration?.enabled) initializator = init()

  return initializator instanceof Promise
    ? initializator.then(() => configurator)
    : configurator
}

/**
 * Injects chromecast plugin and functionality into the player
 */
export function inject(player: TPlayer): TPlayer {
  // do nothing if chromecast is disabled
  const options = player.options_
  if (!options[chromecastKey]?.enabled) return player

  // browser not supported
  if (!chromecast.isChromiumBased()) return player

  // initializes the chromecast plugin on the player
  player.chromecast({ receiverAppID: '41366003' })

  return player
}
